import React, { useEffect, useRef, useState } from 'react';
import { 
  FaChalkboardTeacher, FaUsers, FaRegSun, FaFan,
  FaLaptop, FaDesktop, FaChair, FaWifi,
  FaArrowRight, FaStar, FaCheckCircle, FaRegBuilding,
  FaRegLightbulb, FaThermometerHalf, FaMicrophone,
  FaPlug, FaRegClock, FaBookOpen, FaVideo, FaTv,
  FaProjectDiagram
} from 'react-icons/fa';
import { MdOutlineClass, MdAirlineSeatReclineNormal } from 'react-icons/md';
import { IoMdSchool } from 'react-icons/io';

const ClassRoomSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: <FaChalkboardTeacher />, title: "Modern Teaching Aids", desc: "Smart boards and digital learning tools", color: "from-yellow-400 to-yellow-500" },
    { icon: <MdAirlineSeatReclineNormal />, title: "Comfortable Seating", desc: "Ergonomic chairs for better focus", color: "from-orange-400 to-orange-500" },
    { icon: <FaRegSun />, title: "Well-Ventilated", desc: "Proper air circulation and natural light", color: "from-blue-400 to-blue-500" },
    { icon: <FaUsers />, title: "Interactive Learning", desc: "Optimal student-teacher interaction", color: "from-green-400 to-green-500" }
  ];

  const amenities = [
    { icon: <FaProjectDiagram />, name: "Projectors", available: true },
    { icon: <FaLaptop />, name: "Smart Boards", available: true },
    { icon: <FaDesktop />, name: "Computer Systems", available: true },
    { icon: <FaWifi />, name: "Wi-Fi Enabled", available: true },
    { icon: <FaMicrophone />, name: "Audio System", available: true },
    { icon: <FaPlug />, name: "Power Backup", available: true }
  ];

  const classroomImages = [
    { id: 1, src: "image1.png", name: "Smart Classroom" },
    { id: 2, src: "image4.png", name: "Lecture Hall" },
    { id: 3, src: "image5.png", name: "Seminar Room" },
    { id: 4, src: "image7.png", name: "Digital Classroom" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
            <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
            <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-orange-500 font-semibold">ClassRoom</span>
          </div>

          {/* Heading */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-2xl shadow-lg">
                <MdOutlineClass className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                Our Classrooms
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">
              Modern, spacious, and technology-enabled learning spaces
            </p>
          </div>

          {/* Main Content */}
          <div className={`grid lg:grid-cols-2 gap-10 mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '100ms' }}>
            
            {/* Left Side - Description */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FaRegBuilding className="text-yellow-500" />
                  About Our Classrooms
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  BNS Mahila Mahavidyalaya boasts well-designed classrooms that foster an ideal learning environment. 
                  Equipped with modern teaching aids, each classroom is <span className="font-semibold text-orange-600">spacious, well-ventilated, and comfortable</span>.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mt-3">
                  The seating arrangements are designed to ensure optimal interaction between students and instructors. 
                  These classrooms are dedicated to enhancing the educational experience and promoting effective learning.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mt-3">
                  Every classroom is equipped with state-of-the-art technology including smart boards, projectors, 
                  and audio systems to support modern teaching methodologies and interactive learning.
                </p>
              </div>

              {/* Amenities Grid */}
              <div className="bg-white rounded-2xl p-5 shadow-lg">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  Classroom Amenities
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {amenities.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-600 text-sm">
                      <div className="text-green-500 text-xs">✓</div>
                      <div className="flex items-center gap-2">
                        {item.icon}
                        <span>{item.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Features */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <FaStar className="text-yellow-200" />
                  Classroom Features
                </h3>
                <div className="space-y-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                      <div className={`bg-gradient-to-r ${feature.color} p-2 rounded-lg text-white`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{feature.title}</h4>
                        <p className="text-xs text-white/80">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl p-3 text-center shadow-md">
                  <div className="text-yellow-500 text-xl mb-1 flex justify-center">
                    <FaChair />
                  </div>
                  <div className="text-lg font-bold text-gray-800">50+</div>
                  <div className="text-gray-500 text-xs">Seats per Class</div>
                </div>
                <div className="bg-white rounded-xl p-3 text-center shadow-md">
                  <div className="text-yellow-500 text-xl mb-1 flex justify-center">
                    <FaRegClock />
                  </div>
                  <div className="text-lg font-bold text-gray-800">8 AM - 5 PM</div>
                  <div className="text-gray-500 text-xs">Class Timing</div>
                </div>
              </div>
            </div>
          </div>

          {/* Classroom Gallery */}
          <div className={`mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '200ms' }}>
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center flex items-center justify-center gap-2">
              <IoMdSchool className="text-yellow-500 text-2xl" />
              Classroom Gallery
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {classroomImages.map((image, idx) => (
                <div key={idx} className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300">
                  <img 
                    src={image.src}
                    alt={image.name}
                    className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-2">
                    <p className="text-white text-xs font-semibold">{image.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Highlights */}
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '300ms' }}>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaRegLightbulb className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Smart Lighting</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaThermometerHalf className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">AC Classrooms</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaBookOpen className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Digital Library Access</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-md hover:shadow-lg transition-all">
              <FaUsers className="text-yellow-500 text-xl mx-auto mb-1" />
              <p className="text-gray-700 text-xs font-semibold">Group Discussion Areas</p>
            </div>
          </div>

          {/* Quote Section */}
          <div className={`bg-gradient-to-r from-yellow-100 via-orange-50 to-blue-100 rounded-2xl p-6 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '400ms' }}>
            <p className="text-gray-700 text-sm italic">
              "A classroom is not just a room, it's a space where dreams take shape and futures are built."
            </p>
            <div className="flex justify-center gap-1 mt-2">
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
              <FaStar className="text-yellow-400 text-xs" />
            </div>
          </div>

          {/* CTA Button */}
          <div className={`text-center mt-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '500ms' }}>
            <button className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
              Take a Virtual Tour
              <FaArrowRight className="text-sm" />
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default ClassRoomSection;